import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Loader2, Users } from "lucide-react";
import api from "@/lib/api";
import { useSite } from "@/lib/site";
import Navbar from "@/components/Navbar";
import { SiteFooter } from "@/components/Brand";
import { MentorCard } from "@/components/MentorCard";
import { Input } from "@/components/ui/input";

const Mentors = () => {
  const site = useSite();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [data, setData] = useState(null);
  const tag = params.get("tag") || "";

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/mentors", { params: { q, tag } }).then((r) => setData(r.data)).catch(() => setData({ mentors: [], tags: [] }));
    }, 200);
    return () => clearTimeout(t);
  }, [q, tag]);

  const setTag = (t) => { const p = new URLSearchParams(params); if (t) p.set("tag", t); else p.delete("tag"); setParams(p); };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <section className="bg-[#0B2F55]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-sky-300">Connect to a mentor</p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-[#F5F1E8] mt-3 leading-tight" data-testid="mentors-title">Find the person who'll<br className="hidden sm:block" /> push you in the right direction.</h1>
          <p className="font-body text-base md:text-lg text-[#F5F1E8]/70 mt-5 max-w-2xl">Book a single session or a monthly mentorship. Pay via UPI, and we'll introduce you personally.</p>
          <div className="relative mt-8 max-w-xl">
            <Search className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, role or skill…" data-testid="mentor-search-input"
              className="h-13 py-3.5 pl-12 rounded-full bg-white border-0 text-base" />
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {data?.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="mentor-tags">
            <button onClick={() => setTag("")} data-testid="tag-all"
              className={`font-body text-sm px-3.5 py-1.5 rounded-full border transition-colors ${!tag ? "bg-[#0B2F55] text-white border-[#0B2F55]" : "bg-white text-slate-600 border-slate-200 hover:border-primary"}`}>All</button>
            {data.tags.map((t) => (
              <button key={t} onClick={() => setTag(t === tag ? "" : t)} data-testid={`tag-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className={`font-body text-sm px-3.5 py-1.5 rounded-full border transition-colors ${tag === t ? "bg-[#0B2F55] text-white border-[#0B2F55]" : "bg-white text-slate-600 border-slate-200 hover:border-primary"}`}>{t}</button>
            ))}
          </div>
        )}
        {!data ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : data.mentors.length === 0 ? (
          <div className="bg-white rounded-3xl border border-dashed border-slate-300 p-14 text-center" data-testid="mentors-empty">
            <Users className="w-10 h-10 text-slate-300 mx-auto mb-4" />
            <p className="font-heading font-bold text-lg text-slate-900">{data.total === 0 ? "Our first mentors are being onboarded" : "No mentors match that search"}</p>
            <p className="font-body text-sm text-slate-500 mt-2">{data.total === 0 ? "Check back soon — or apply to become one of them." : "Try a different keyword or clear the filter."}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="mentor-grid">
            {data.mentors.map((m, i) => <MentorCard key={m.mentor_id} mentor={m} index={i} currency={site.currency} />)}
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
};

export default Mentors;
